// Dependencies
import { FC, useState, MouseEvent, MouseEventHandler } from "react";

// Components
import { IconButton } from "~/components/Common";
import { HashLoading } from "~/components/Common/Loading";
import { If, Choose, When } from "jsx-control-statements";
import Tag from "~/components/Common/Tag";
import WalletOptions from "../WalletOptions";
import WalletInfoItem from "~/components/Layout/InfoItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Icons
import { ReactComponent as OptionIcon } from "~/assets/icons/options.svg";
import { ReactComponent as EditIcon } from "~/assets/icons/pen-to-square-regular.svg";
import { ReactComponent as ShareIcon } from "~/assets/icons/share-from-square-regular.svg";
// utils
import { convertISOtoLocalTime, currencyFormat } from "~/utils";

// Types
import { WALLET_TYPE } from "~/enums";
import { TQueryStatus, TWalletDetailData } from "~/types/types";

// api

// Styles
import {
  StyledItemText,
  StyledInfoLabel,
  StyledInfoDetail,
  StyledInfoAction,
  StyledInfoWrapper,
  StyledInfoContent,
  StyledItemCurrency,
} from "~/components/Layout/LayoutInfo/";
import { StyledCateView } from "./WalletInfo.styled";
import { StyledErrorMessage } from "~/components/Layout";

interface WalletInfoProps {
  data: TWalletDetailData | undefined;
  error: string | undefined;
  status: TQueryStatus;
  onEdit: MouseEventHandler;
  onDelete: MouseEventHandler;
  onAddMember: MouseEventHandler;
  onShowCategory: MouseEventHandler;
}

const WalletInfo: FC<WalletInfoProps> = ({
  data,
  error,
  status,
  onEdit,
  onDelete,
  onAddMember,
  onShowCategory,
}) => {
  // states
  // for options menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openOptions = Boolean(anchorEl);

  // Methods

  /**  Options Menu */
  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  /** End of Options Menu */

  return (
    <StyledInfoWrapper>
      <StyledInfoLabel>Wallet Detail</StyledInfoLabel>
      <StyledInfoContent>
        <Choose>
          <When condition={status.isLoading}>
            <HashLoading />
          </When>
          <When condition={status.isError}>
            <StyledErrorMessage>Error: {error}</StyledErrorMessage>
          </When>
          <When condition={status.isSuccess}>
            <StyledInfoAction>
              <IconButton
                onClick={handleOpenMenu}
                aria-controls={openOptions ? "wallet-menu" : undefined}
                aria-expanded={openOptions ? "true" : undefined}
              >
                <OptionIcon />
              </IconButton>
              <IconButton onClick={onEdit}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => {}}>
                <ShareIcon />
              </IconButton>
            </StyledInfoAction>
            <StyledInfoDetail>
              <WalletInfoItem
                label={"Name"}
                data={<StyledItemText>{data?.name}</StyledItemText>}
              />
              <WalletInfoItem
                label={"Type"}
                data={
                  <Tag
                    label={data?.type}
                    color={WALLET_TYPE[data?.type || "Basic"]?.tagColor}
                  />
                }
              />
              <WalletInfoItem
                label={"Currency"}
                data={<StyledItemText>{data?.currencyId}</StyledItemText>}
              />
              <WalletInfoItem
                label={"Exclude from Total"}
                data={
                  <StyledItemText>
                    {data?.excludeFromTotal ? "Yes" : "No"}
                  </StyledItemText>
                }
              />

              {/* For Gold wallet */}
              <If condition={data?.type === "Goal"}>
                <WalletInfoItem
                  label={"Gold value"}
                  data={
                    <StyledItemCurrency>
                      {currencyFormat(data?.goalValue, data?.currencyId)}
                    </StyledItemCurrency>
                  }
                />
                <WalletInfoItem
                  label={"Starting Amount"}
                  data={
                    <StyledItemCurrency>
                      {currencyFormat(data?.startingAmount, data?.currencyId)}
                    </StyledItemCurrency>
                  }
                />
                <WalletInfoItem
                  label={"Ending Date"}
                  data={
                    <StyledItemText>
                      {convertISOtoLocalTime(data?.endingDate)}
                    </StyledItemText>
                  }
                />
                <WalletInfoItem
                  label={"Enable notification"}
                  data={
                    <StyledItemText>
                      {data?.enableNotify ? "Yes" : "No"}
                    </StyledItemText>
                  }
                />
              </If>

              {/* For Basic wallet */}
              <If condition={data?.type === "Basic"}>
                <WalletInfoItem
                  label={"Initial Balance"}
                  data={
                    <StyledItemCurrency>
                      {currencyFormat(data?.initialBalance, data?.currencyId)}
                    </StyledItemCurrency>
                  }
                />
              </If>
            </StyledInfoDetail>
            <StyledCateView variant={"h2"} onClick={onShowCategory}>
              Categories
              <ChevronRightIcon />
            </StyledCateView>
          </When>
        </Choose>
      </StyledInfoContent>
      <WalletOptions
        open={openOptions}
        anchorEl={anchorEl}
        id="wallet-menu"
        onClose={handleCloseMenu}
        onDelete={onDelete}
        onAddMember={onAddMember}
      />
    </StyledInfoWrapper>
  );
};

export default WalletInfo;
